import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '../components/Navbar';

const mockDonors = {
  1: { id: 1, donor_code: "1234", height_cm: 178, weight_kg: 79, eye_color: "Blue", hair_color: "Brown", ethnicity: "Caucasian", blood_type: "O+", education: "PhD Candidate", occupation: "Research Scientist", personality: "Analytical and curious", interests: ["Science", "Reading", "Hiking"], bio: "Passionate about genetic research and helping families grow.", available: true },
  2: { id: 2, donor_code: "5678", height_cm: 173, weight_kg: 73, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "A+", education: "MD", occupation: "Physician", personality: "Compassionate and dedicated", interests: ["Medicine", "Music", "Travel"], bio: "Dedicated doctor wanting to help others build families.", available: true },
  3: { id: 3, donor_code: "9012", height_cm: 183, weight_kg: 82, eye_color: "Brown", hair_color: "Black", ethnicity: "Hispanic", blood_type: "B+", education: "BS Engineering", occupation: "Engineer", personality: "Creative problem-solver", interests: ["Technology", "Sports", "Cooking"], bio: "Engineer with a passion for innovation and helping others.", available: true },
  4: { id: 4, donor_code: "3456", height_cm: 180, weight_kg: 84, eye_color: "Brown", hair_color: "Black", ethnicity: "African American", blood_type: "O-", education: "MBA", occupation: "Business Analyst", personality: "Strategic thinker", interests: ["Finance", "Fitness", "Photography"], bio: "Business professional focused on strategic planning and community.", available: false },
  5: { id: 5, donor_code: "7890", height_cm: 175, weight_kg: 77, eye_color: "Green", hair_color: "Blond", ethnicity: "Caucasian", blood_type: "A-", education: "MS Biology", occupation: "Biologist", personality: "Detail-oriented", interests: ["Nature", "Research", "Cycling"], bio: "Biologist studying ecosystems and conservation.", available: true },
  6: { id: 6, donor_code: "2345", height_cm: 170, weight_kg: 70, eye_color: "Brown", hair_color: "Black", ethnicity: "Asian", blood_type: "AB+", education: "PhD Physics", occupation: "Physicist", personality: "Analytical and precise", interests: ["Physics", "Chess", "Running"], bio: "Physicist exploring the fundamental laws of the universe.", available: true },
};

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border/60 py-2">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium">{v}</span>
    </div>
  );
}

function DonorDetail() {
  const { donorId } = useParams();
  const donor = mockDonors[donorId];
  const [subsidyOpen, setSubsidyOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (!donor) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-display text-3xl">Donor not found</h1>
          <Link to="/donors" className="mt-4 inline-block text-primary underline">Back to catalog</Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 sm:py-14">
        <Link to="/donors" className="text-sm text-muted-foreground hover:text-foreground">← Back to catalog</Link>

        <div className="mt-6 grid md:grid-cols-[320px_1fr] gap-8 lg:gap-12">
          <div className="space-y-4">
            <div className="aspect-square rounded-2xl border border-border bg-secondary flex items-center justify-center">
              <span className="text-8xl text-primary/30 font-display">{donor.donor_code.charAt(0)}</span>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
              <div className="text-sm font-medium">Pricing</div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Standard vial</span>
                <span className="font-medium">$1,950</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">With subsidy</span>
                <span className="font-medium text-primary">$1,000 – $1,200</span>
              </div>
              <button
                onClick={() => {
                  setSubsidyOpen(true);
                  console.log('Apply for subsidy clicked for donor:', donor.donor_code);
                }}
                className="w-full rounded-full bg-primary text-primary-foreground py-2.5 text-sm hover:opacity-90 transition"
              >Apply for subsidy</button>
              <button 
                onClick={() => console.log('Reserve vial clicked for donor:', donor.donor_code)}
                className="w-full rounded-full border border-border py-2.5 text-sm hover:bg-secondary transition"
              >Reserve vial</button>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-4xl sm:text-6xl">{donor.donor_code}</h1>
              <span className={`text-xs rounded-full px-3 py-1 ${donor.available ? 'bg-accent' : 'bg-muted'}`}>{donor.available ? "Available" : "Reserved"}</span>
            </div>
            <p className="mt-2 text-muted-foreground">{donor.occupation} · {donor.education}</p>

            <section className="mt-8">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground">About</h2>
              <p className="mt-3 text-foreground/90 leading-relaxed">{donor.bio}</p>
            </section>

            <section className="mt-8">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Video interview</h2>
              <div className="mt-3 aspect-video rounded-2xl border border-border bg-secondary flex items-center justify-center text-sm text-muted-foreground">
                Secure video interview available on request
              </div>
            </section>

            <section className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
              <Row k="Ethnicity" v={donor.ethnicity} />
              <Row k="Blood type" v={donor.blood_type} />
              <Row k="Eye color" v={donor.eye_color} />
              <Row k="Hair color" v={donor.hair_color} />
              <Row k="Height" v={`${donor.height_cm} cm`} />
              <Row k="Weight" v={`${donor.weight_kg} kg`} />
              <Row k="Personality" v={donor.personality} />
            </section>

            <section className="mt-8">
              <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Interests</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {donor.interests.map((i) => (
                  <span key={i} className="text-sm rounded-full bg-secondary px-3 py-1.5 text-secondary-foreground">{i}</span>
                ))}
              </div>
            </section>

            <section className="mt-10 rounded-2xl border border-border bg-secondary/40 p-6 text-sm text-muted-foreground">
              All donors are routed through our FDA-registered, physician-supervised facility. Known and directed donor arrangements are supported with secure video-chat interviewing.
            </section>
          </div>
        </div>
      </div>

      {subsidyOpen && (
        <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSubsidyOpen(false)}>
          <div className="bg-card rounded-2xl border border-border max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            {submitted ? (
              <div className="text-center py-6">
                <h3 className="font-display text-2xl">Application sent</h3>
                <p className="mt-2 text-sm text-muted-foreground">Donor {donor.donor_code} will review your application and respond within 5 business days.</p>
                <button onClick={() => { setSubsidyOpen(false); setSubmitted(false); }} className="mt-6 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm">Close</button>
              </div>
            ) : (
              <form onSubmit={(e) => { 
                e.preventDefault(); 
                setSubmitted(true); 
                console.log('Subsidy application submitted for donor:', donor.donor_code);
              }} className="space-y-4">
                <div>
                  <h3 className="font-display text-2xl">Apply for subsidy</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Share a little about yourself. {donor.donor_code} reviews each application personally.</p>
                </div>
                <input required placeholder="Full name" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                <input required type="email" placeholder="Email" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                <textarea required rows={4} placeholder="Tell the donor your story" className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm" />
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setSubsidyOpen(false)} className="rounded-full px-4 py-2 text-sm hover:bg-secondary">Cancel</button>
                  <button type="submit" className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm">Submit</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DonorDetail;
